import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Section from '../common/Section';
import ParallaxText from '../ui/ParallaxText';
import { CONTACT_INFO } from '../../utils/constants';
import { Mail, MessageCircle, Send, Copy, Check } from 'lucide-react';

const Contact: React.FC = () => {
    const [copiedItem, setCopiedItem] = useState<string | null>(null);
    const [isHovered, setIsHovered] = useState<string | null>(null);

    const handleCopy = (value: string, platform: string) => {
        navigator.clipboard.writeText(value);
        setCopiedItem(platform);
        setTimeout(() => setCopiedItem(null), 2000);
    };

    const getIcon = (platform: string) => {
        switch (platform.toLowerCase()) {
            case 'mail':
                return Mail;
            case 'discord':
                return MessageCircle;
            default:
                return Send;
        }
    };

    return (
        <Section id="contact" className="relative overflow-hidden">
            {/* Updated Background with Smoother Transition */}
            <div className="absolute inset-0">
                {/* Main gradient background */}
                <div className="absolute inset-0 bg-gradient-to-b from-white via-sky-50/30 to-slate-50/80" />

                {/* Animated Background */}
                <div className="absolute inset-0 overflow-hidden">
                    <motion.div
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]"
                        animate={{
                            rotate: 360,
                        }}
                        transition={{
                            duration: 50,
                            repeat: Infinity,
                            ease: "linear",
                        }}
                    >
                        <div className="w-full h-full bg-gradient-conic from-sky-400/20 via-purple-400/20 to-sky-400/20 rounded-full filter blur-3xl" />
                    </motion.div>
                </div>
            </div>

            <div className="relative z-10">
                <ParallaxText offset={30}>
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="h2-display text-slate-900 mb-4">
                            <span className="text-gradient">Let's Connect</span>
                        </h2>
                        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                            Have a project in mind or just want to say hello? I'd love to hear from you!
                        </p>
                        <div className="w-24 h-1 mx-auto bg-gradient-to-r from-sky-400 to-purple-400 rounded-full mt-6" />
                    </motion.div>
                </ParallaxText>

                {/* Contact Cards */}
                <div className="max-w-4xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
                        {CONTACT_INFO.map((contact, index) => {
                            const Icon = getIcon(contact.platform);

                            return (
                                <motion.div
                                    key={contact.platform}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 0.6,
                                        delay: index * 0.1,
                                        ease: [0.19, 1, 0.22, 1],
                                    }}
                                    onMouseEnter={() => setIsHovered(contact.platform)}
                                    onMouseLeave={() => setIsHovered(null)}
                                    className="relative group"
                                >
                                    {/* Glow Effect */}
                                    <AnimatePresence>
                                        {isHovered === contact.platform && (
                                            <motion.div
                                                className="absolute inset-0 bg-gradient-to-br from-sky-400/20 to-purple-400/20 rounded-2xl filter blur-xl"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.3 }}
                                            />
                                        )}
                                    </AnimatePresence>

                                    <div
                                        className="relative rounded-2xl p-8 shadow-xl border transition-all duration-300"
                                        style={{
                                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                                            backdropFilter: 'blur(15px) saturate(110%)',
                                            WebkitBackdropFilter: 'blur(15px) saturate(110%)',
                                            borderColor: isHovered === contact.platform ? 'rgba(14, 165, 233, 0.3)' : 'rgba(14, 165, 233, 0.1)',
                                            boxShadow: isHovered === contact.platform
                                                ? '0 25px 50px rgba(14, 165, 233, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                                                : '0 8px 25px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                        }}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-4">
                                                <motion.div
                                                    className="p-3 rounded-lg bg-gradient-to-br from-sky-400 to-sky-500 text-white"
                                                    whileHover={{ rotate: 360 }}
                                                    transition={{ duration: 0.5 }}
                                                >
                                                    <Icon size={24} />
                                                </motion.div>

                                                <div className="flex-1">
                                                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                                                        {contact.platform}
                                                    </h3>
                                                    <p className="text-slate-700 font-mono text-sm break-all">
                                                        {contact.value}
                                                    </p>
                                                </div>
                                            </div>

                                            <motion.button
                                                onClick={() => handleCopy(contact.value, contact.platform)}
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                                className="p-2 rounded-lg hover:bg-sky-50 transition-colors"
                                            >
                                                <AnimatePresence mode="wait">
                                                    {copiedItem === contact.platform ? (
                                                        <motion.div
                                                            key="check"
                                                            initial={{ scale: 0 }}
                                                            animate={{ scale: 1 }}
                                                            exit={{ scale: 0 }}
                                                        >
                                                            <Check size={20} className="text-green-500" />
                                                        </motion.div>
                                                    ) : (
                                                        <motion.div
                                                            key="copy"
                                                            initial={{ scale: 0 }}
                                                            animate={{ scale: 1 }}
                                                            exit={{ scale: 0 }}
                                                        >
                                                            <Copy size={20} className="text-slate-400" />
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </motion.button>
                                        </div>

                                        {contact.link && (
                                            <motion.a
                                                href={contact.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 mt-4 text-sm text-sky-600 hover:text-sky-700 font-medium"
                                                whileHover={{ x: 5 }}
                                            >
                                                Open in Mail App
                                                <Send size={16} />
                                            </motion.a>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Smooth Bottom Transition to Footer */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent via-slate-50/50 to-slate-100/80 z-10" />
        </Section>
    );
};

export default Contact;